import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '@/services/api';

function Spinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export default function SimulatorPage() {
  const [params, setParams] = useState({
    days: 365,
    new_users_per_day: 2,
    recirculation_rate: 0.30,
    subscription_rate: 0.20,
    box_size: 11,
    initial_fund: 100,
    runs: 100,
  });
  const [result, setResult] = useState<any>(null);

  // ── Fetch presets ──
  const { data: presetsData, isLoading } = useQuery({
    queryKey: ['simulator-presets'],
    queryFn: () => api.get('/simulator/presets').then((r) => r.data),
  });

  // ── Run simulation ──
  const runMutation = useMutation({
    mutationFn: (p: typeof params) => api.post('/simulator/run', p).then((r) => r.data),
    onSuccess: (data) => setResult(data),
  });

  const loadPreset = (presetId: string) => {
    const preset = (presetsData?.presets ?? []).find((p: any) => p.id === presetId);
    if (preset) {
      setParams({
        days: preset.days,
        new_users_per_day: preset.new_users_per_day,
        recirculation_rate: preset.recirculation_rate,
        subscription_rate: preset.subscription_rate,
        box_size: preset.box_size,
        initial_fund: preset.initial_fund,
        runs: preset.runs,
      });
    }
  };

  const verdictColors: Record<string, string> = {
    green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    yellow: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-surface-900 dark:text-white">🎲 Simulatore Monte Carlo</h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Simula il comportamento del sistema e verifica la sostenibilità a lungo termine
        </p>
      </div>

      {/* Presets */}
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-surface-700 dark:text-surface-300 mb-3">Scenari pre-configurati</h3>
        <div className="flex flex-wrap gap-2">
          {(presetsData?.presets ?? []).map((preset: any) => (
            <button
              key={preset.id}
              onClick={() => loadPreset(preset.id)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium bg-surface-100 dark:bg-surface-800 text-surface-700 dark:text-surface-300 hover:bg-surface-200 dark:hover:bg-surface-700 transition-colors"
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
        </div>
      </div>

      {/* Parameters */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4">Parametri simulazione</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-surface-500 dark:text-surface-400 mb-1">Giorni</label>
            <input
              type="number" min={30} max={730}
              value={params.days}
              onChange={(e) => setParams({ ...params, days: Number(e.target.value) })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-surface-500 dark:text-surface-400 mb-1">Nuovi utenti/giorno</label>
            <input
              type="number" min={0} max={50} step={0.5}
              value={params.new_users_per_day}
              onChange={(e) => setParams({ ...params, new_users_per_day: Number(e.target.value) })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-surface-500 dark:text-surface-400 mb-1">Tasso ricircolo (%)</label>
            <input
              type="number" min={0} max={100} step={5}
              value={Math.round(params.recirculation_rate * 100)}
              onChange={(e) => setParams({ ...params, recirculation_rate: Number(e.target.value) / 100 })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-surface-500 dark:text-surface-400 mb-1">Subscription rate (%)</label>
            <input
              type="number" min={0} max={100} step={5}
              value={Math.round(params.subscription_rate * 100)}
              onChange={(e) => setParams({ ...params, subscription_rate: Number(e.target.value) / 100 })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-surface-500 dark:text-surface-400 mb-1">Box size</label>
            <select
              value={params.box_size}
              onChange={(e) => setParams({ ...params, box_size: Number(e.target.value) })}
              className="input w-full"
            >
              <option value={5}>Micro (5)</option>
              <option value={7}>Compact (7)</option>
              <option value={11}>Standard (11)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-surface-500 dark:text-surface-400 mb-1">Fondo iniziale (€)</label>
            <input
              type="number" min={0} max={10000}
              value={params.initial_fund}
              onChange={(e) => setParams({ ...params, initial_fund: Number(e.target.value) })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-surface-500 dark:text-surface-400 mb-1">Simulazioni (runs)</label>
            <input
              type="number" min={10} max={500}
              value={params.runs}
              onChange={(e) => setParams({ ...params, runs: Number(e.target.value) })}
              className="input w-full"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() => runMutation.mutate(params)}
              disabled={runMutation.isPending}
              className="btn-primary w-full py-2 rounded-xl text-sm font-semibold"
            >
              {runMutation.isPending ? '⏳ Simulazione...' : '🚀 Avvia'}
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {result && (
        <>
          {/* Verdict */}
          <div className={`card p-6 ${verdictColors[result.health_verdict?.color ?? 'green']}`}>
            <div className="flex items-center gap-3">
              <span className="text-3xl">
                {result.health_verdict?.status === 'HEALTHY' ? '✅'
                  : result.health_verdict?.status === 'WARNING' ? '⚠️'
                  : result.health_verdict?.status === 'DANGER' ? '🟠'
                  : '🔴'}
              </span>
              <div>
                <h3 className="text-lg font-bold">{result.health_verdict?.status}</h3>
                <p className="text-sm">{result.health_verdict?.message}</p>
              </div>
            </div>
          </div>

          {/* Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-red-500">{result.summary?.block_probability}%</div>
              <div className="text-xs text-surface-500 dark:text-surface-400">Probabilità blocco</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                €{result.summary?.fund_balance?.median?.toFixed(0) ?? 0}
              </div>
              <div className="text-xs text-surface-500 dark:text-surface-400">Fondo mediano</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                {result.summary?.completed_boxes?.avg?.toFixed(0) ?? 0}
              </div>
              <div className="text-xs text-surface-500 dark:text-surface-400">Box completati (media)</div>
            </div>
            <div className="card p-4 text-center">
              <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                {result.summary?.avg_completion_days ?? '—'}g
              </div>
              <div className="text-xs text-surface-500 dark:text-surface-400">Completamento medio</div>
            </div>
          </div>

          {/* Detailed results */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4">Dettagli simulazione</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-surface-500 dark:text-surface-400">Runs totali:</span>
                <span className="ml-2 text-surface-900 dark:text-white font-medium">{result.summary?.total_runs}</span>
              </div>
              <div>
                <span className="text-surface-500 dark:text-surface-400">Runs bloccati:</span>
                <span className="ml-2 text-red-600 dark:text-red-400 font-medium">{result.summary?.blocked_runs}</span>
              </div>
              <div>
                <span className="text-surface-500 dark:text-surface-400">Fondo min/max:</span>
                <span className="ml-2 text-surface-900 dark:text-white font-medium">
                  €{result.summary?.fund_balance?.min?.toFixed(0)} — €{result.summary?.fund_balance?.max?.toFixed(0)}
                </span>
              </div>
              <div>
                <span className="text-surface-500 dark:text-surface-400">Box min/max:</span>
                <span className="ml-2 text-surface-900 dark:text-white font-medium">
                  {result.summary?.completed_boxes?.min} — {result.summary?.completed_boxes?.max}
                </span>
              </div>
              <div>
                <span className="text-surface-500 dark:text-surface-400">Merge medi:</span>
                <span className="ml-2 text-surface-900 dark:text-white font-medium">{result.summary?.merged_boxes_avg}</span>
              </div>
              <div>
                <span className="text-surface-500 dark:text-surface-400">Payout medio:</span>
                <span className="ml-2 text-surface-900 dark:text-white font-medium">€{result.summary?.total_payouts_avg?.toFixed(0)}</span>
              </div>
            </div>
          </div>

          {/* Timeline snapshot */}
          {result.sample_timeline?.length > 0 && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white mb-4">📈 Timeline (campione)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-surface-200 dark:border-surface-700">
                      <th className="py-1 text-left text-surface-500 dark:text-surface-400">Giorno</th>
                      <th className="py-1 text-right text-surface-500 dark:text-surface-400">Utenti</th>
                      <th className="py-1 text-right text-surface-500 dark:text-surface-400">Box aperti</th>
                      <th className="py-1 text-right text-surface-500 dark:text-surface-400">Completati</th>
                      <th className="py-1 text-right text-surface-500 dark:text-surface-400">Fondo (€)</th>
                      <th className="py-1 text-right text-surface-500 dark:text-surface-400">Payout (€)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.sample_timeline.map((snap: any, i: number) => (
                      <tr key={i} className="border-b border-surface-100 dark:border-surface-800">
                        <td className="py-1 text-surface-900 dark:text-white">{snap.day}</td>
                        <td className="py-1 text-right text-surface-600 dark:text-surface-300">{snap.total_users}</td>
                        <td className="py-1 text-right text-surface-600 dark:text-surface-300">{snap.open_boxes}</td>
                        <td className="py-1 text-right text-green-600 dark:text-green-400">{snap.completed_boxes}</td>
                        <td className="py-1 text-right text-primary-600 dark:text-primary-400">{snap.fund_balance}</td>
                        <td className="py-1 text-right text-amber-600 dark:text-amber-400">{snap.total_payouts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
